import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Badge,
  Form,
  Row,
  Col,
  Button,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  FaHistory,
  FaFilter,
  FaRedo,
  FaSearch,
  FaCalendarAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import activityLogService from "../../../services/activityLogService";

const ActivityLogsList = () => {
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    role_name: "",
    entity_type: "",
    search: "",
    start_date: "",
    end_date: "",
    limit: 15,
    offset: 0,
  });

  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableEntities, setAvailableEntities] = useState([]);

  useEffect(() => {
    fetchFilterOptions();
    fetchLogs();
  }, [filters.offset, filters.role_name, filters.entity_type]);

  const fetchFilterOptions = async () => {
    try {
      const res = await activityLogService.getFilters();
      if (res.data) {
        setAvailableRoles(res.data.roles || []);
        setAvailableEntities(res.data.entity_types || []);
      }
    } catch (error) {
      console.error("Error loading log filters:", error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await activityLogService.getActivityLogs(filters);
      setLogs(res.data || []);
      setTotalLogs(res.total || 0);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      toast.error("Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, offset: 0 }));
    fetchLogs();
  };

  const handleResetFilters = () => {
    setFilters({
      role_name: "",
      entity_type: "",
      search: "",
      start_date: "",
      end_date: "",
      limit: 15,
      offset: 0,
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getRoleBadgeVariant = (role) => {
    if (!role) return "secondary";
    const r = role.toLowerCase();
    if (r.includes("super admin")) return "dark";
    if (r.includes("product")) return "primary";
    if (r.includes("order")) return "info";
    if (r.includes("support")) return "warning";
    if (r.includes("vendor")) return "success";
    if (r.includes("inventory")) return "secondary";
    return "primary";
  };

  const totalPages = Math.ceil(totalLogs / filters.limit);
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  const handlePageChange = (page) => {
    const newOffset = (page - 1) * filters.limit;
    setFilters((prev) => ({ ...prev, offset: newOffset }));
  };

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2">
            <FaHistory className="text-primary" /> Activity Audit Logs
          </h3>
          <p className="text-muted mb-0">
            Real-time chronological audit trail of all staff activities and administrative operations.
          </p>
        </div>

        <Button
          variant="outline-primary"
          className="d-flex align-items-center gap-2"
          onClick={fetchLogs}
          disabled={loading}
        >
          <FaRedo className={loading ? "fa-spin" : ""} /> Refresh Logs
        </Button>
      </div>

      {/* Filter Bar */}
      <Card className="shadow-sm border-0 mb-4 bg-light">
        <Card.Body>
          <Form onSubmit={handleSearchSubmit}>
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">
                    Search User / Activity
                  </Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <FaSearch className="text-muted" />
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Amit, Added Product..."
                      value={filters.search}
                      onChange={(e) =>
                        setFilters({ ...filters, search: e.target.value })
                      }
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">Filter by Role</Form.Label>
                  <Form.Select
                    value={filters.role_name}
                    onChange={(e) =>
                      setFilters({ ...filters, role_name: e.target.value, offset: 0 })
                    }
                  >
                    <option value="">All Roles</option>
                    <option value="Super Admin">Super Admin</option>
                    <option value="Product Manager">Product Manager</option>
                    <option value="Order Manager">Order Manager</option>
                    <option value="Customer Support">Customer Support</option>
                    <option value="Vendor Manager">Vendor Manager</option>
                    <option value="Inventory Manager">Inventory Manager</option>
                    {availableRoles
                      .filter(
                        (r) =>
                          ![
                            "Super Admin",
                            "Product Manager",
                            "Order Manager",
                            "Customer Support",
                            "Vendor Manager",
                            "Inventory Manager",
                          ].includes(r)
                      )
                      .map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">Entity Type</Form.Label>
                  <Form.Select
                    value={filters.entity_type}
                    onChange={(e) =>
                      setFilters({ ...filters, entity_type: e.target.value, offset: 0 })
                    }
                  >
                    <option value="">All Entities</option>
                    <option value="product">Product</option>
                    <option value="order">Order</option>
                    <option value="refund">Refund</option>
                    <option value="vendor">Vendor</option>
                    <option value="role">Role</option>
                    <option value="user">User</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted">From Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.start_date}
                    onChange={(e) =>
                      setFilters({ ...filters, start_date: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={2} className="d-flex gap-2">
                <Button variant="primary" type="submit" className="w-100">
                  <FaFilter className="me-1" /> Filter
                </Button>
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Activity Table */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
          <h5 className="fw-bold mb-0">Audit Records</h5>
          <span className="text-muted small">Total: {totalLogs} events</span>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Loading audit trail...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaHistory size={40} className="mb-2 text-secondary opacity-50" />
              <p>No activity logs found matching your filters.</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "20%" }}>User</th>
                  <th style={{ width: "20%" }}>Role</th>
                  <th style={{ width: "35%" }}>Activity</th>
                  <th style={{ width: "25%" }} className="text-end">
                    Date & Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <div className="fw-bold text-dark">
                        {log.user_name || "System User"}
                      </div>
                    </td>
                    <td>
                      <Badge
                        bg={getRoleBadgeVariant(log.role_name)}
                        className="px-2 py-1"
                      >
                        {log.role_name || "Admin"}
                      </Badge>
                    </td>
                    <td>
                      <div className="fw-semibold text-dark">{log.activity}</div>
                      {log.comment && (
                        <small className="text-muted d-block">{log.comment}</small>
                      )}
                    </td>
                    <td className="text-end text-muted small">
                      {formatDateTime(log.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>

        {/* Pagination */}
        {totalPages > 1 && (
          <Card.Footer className="bg-white border-top py-3 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-muted small">
              Showing {filters.offset + 1} to{" "}
              {Math.min(filters.offset + filters.limit, totalLogs)} of {totalLogs} entries
            </span>
            <Pagination className="mb-0">
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              />
              {[...Array(totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                // Show first, last, and window around current
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                ) {
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === currentPage}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                } else if (
                  pageNum === currentPage - 3 ||
                  pageNum === currentPage + 3
                ) {
                  return <Pagination.Ellipsis key={pageNum} disabled />;
                }
                return null;
              })}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

export default ActivityLogsList;
